import { createContext, useContext, useState, useEffect, useCallback } from 'react';

import type { ReactNode } from 'react';

// Constants for demo data
const DEMO_CONSTANTS = {
  FIRM_ID: 'demo-firm-001',
  FIRM_NAME: 'Demo Firm',
  TOTAL_CREDITS: 100,
  EXPIRING_CREDITS: 5,
} as const;
import { useAuth } from './AuthContext';
import { useQueryClient } from "@tanstack/react-query";
import { useListAccessibleFirms, type FirmResponse, UserRole, useGetJobAsUser } from '@taxassist/api-client';
import { useLocation } from 'react-router-dom';

export interface FirmContextValue {
  currentFirmId: string | null;
  availableFirms: FirmInfo[];
  setCurrentFirmId: (firmId: string) => void;
  isLoading: boolean;
  error: string | null;
}

export interface FirmInfo {
  firmId: string;
  name: string;
  isActive: boolean;
  totalCredits: number;
  expiringCredits: number;
}

const FirmContext = createContext<FirmContextValue | undefined>(undefined);

interface FirmProviderProps {
  children: ReactNode;
}

/**
 * FirmProvider manages the current firm context for the application.
 * It handles firm selection, user firm permissions, and provides
 * a centralized way to access firm information across components.
 */
export function FirmProvider({ children }: FirmProviderProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentFirmId, setCurrentFirmIdState] = useState<string | null>(null);
  const [availableFirms, setAvailableFirms] = useState<FirmInfo[]>([]);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const jobPathRegex = /\/jobs\/([a-zA-Z0-9-]+)/;
  const match = location.pathname.match(jobPathRegex);
  const jobId = match?.[1] ?? null;
  const firmKnown = location.state?.firmKnown;

  // Fetch job data, but only if we land on a job URL without a known firm
  const {
    data: job,
    isLoading: jobLoading,
  } = useGetJobAsUser(
    currentFirmId as string,
    jobId as string,
    {
    query: {
      enabled: !!jobId && !!currentFirmId && !firmKnown,
      refetchOnWindowFocus: false,
      queryKey: ["job", jobId],
    },
  });

  // Only fetch firms via API for roles that need dynamic firm listing (admin/reviewer)
  // Firm users should use JWT token firmIds directly for better performance and security
  const shouldFetchFirms = user?.role === UserRole.admin || user?.role === UserRole.reviewer;
  
  const { data: firmsData, isLoading: firmsLoading, error: firmsError } = useListAccessibleFirms(
    undefined,
    {
      query: {
        enabled: isAuthenticated && !!user && shouldFetchFirms,
        staleTime: 2 * 60 * 1000, // 2 minutes
        queryKey: ['accessible-firms', user?.userId],
      }
    }
  );

  const isLoading = authLoading || jobLoading || (isAuthenticated && shouldFetchFirms && firmsLoading);

  const initializeFirmContext = useCallback(() => {
    setError(null);


    try {
      if (!isAuthenticated) {
        // Not authenticated - use demo firm only in development
        if (process.env.NODE_ENV === 'development') {
          const demoFirm: FirmInfo = {
            firmId: DEMO_CONSTANTS.FIRM_ID,
            name: DEMO_CONSTANTS.FIRM_NAME,
            isActive: true,
            totalCredits: DEMO_CONSTANTS.TOTAL_CREDITS,
            expiringCredits: DEMO_CONSTANTS.EXPIRING_CREDITS,
          };
          setAvailableFirms([demoFirm]);
          setCurrentFirmIdState(demoFirm.firmId);
        } else {
          setError('Authentication required');
        }
        return;
      }

      if (!user) {
        setError('User information not available');
        return;
      }

      if (shouldFetchFirms) {
        // Admin/Reviewer roles: Use API-fetched firms
        
        if (firmsError) {
          console.error('❌ Firms API Error:', firmsError);
          setError(firmsError.message || 'Failed to load firm information');
          return;
        }

        if (firmsData?.firms && firmsData.firms.length > 0) {
          // Convert API response to FirmInfo format
          const firms: FirmInfo[] = firmsData.firms.map((firm: FirmResponse) => ({
            firmId: firm.firmId,
            name: firm.name,
            isActive: firm.isActive,
            totalCredits: firm.totalCredits,
            expiringCredits: firm.expiringCredits,
          }));

          setAvailableFirms(firms);

          if (jobId && !firmKnown) {
            // If on a job URL, fetch the job to determine the firm
            setFirmFromUrl(jobId, firms);
          } else {
            setDefaultFirm(firms);
          }
        } else {
          setAvailableFirms([]);
          setCurrentFirmIdState(null);
        }
      } else {
        // Firm User/Admin roles: Use JWT token firmIds directly (no API call needed)
        
        if (user.firmIds && user.firmIds.length > 0) {
          // Create firms from token firmIds with generic data
          // Note: Real firm data (name, credits) would come from separate API calls if needed
          const tokenFirms: FirmInfo[] = user.firmIds.map((firmId) => ({
            firmId,
            name: `Firm ${firmId}`, // Generic name - could be enhanced with additional API call
            isActive: true,
            totalCredits: 0, // Would need separate API call to get real credit data
            expiringCredits: 0,
          }));
          
          setAvailableFirms(tokenFirms);

          if (jobId && !firmKnown) {
            // If on a job URL, fetch the job to determine the firm
            setFirmFromUrl(jobId, tokenFirms);
          } else {
            setDefaultFirm(tokenFirms);
          }
        } else {
          setError('No firms assigned to this user');
          setAvailableFirms([]);
          setCurrentFirmIdState(null);
        }
      }
    } catch (error) {
      console.error('Failed to initialize firm context:', error);
      setError('Failed to load firm information');
      
      // Fallback to demo firm only in development
      if (process.env.NODE_ENV === 'development') {
        const demoFirm: FirmInfo = {
          firmId: DEMO_CONSTANTS.FIRM_ID,
          name: DEMO_CONSTANTS.FIRM_NAME,
          isActive: true,
          totalCredits: DEMO_CONSTANTS.TOTAL_CREDITS,
          expiringCredits: DEMO_CONSTANTS.EXPIRING_CREDITS,
        };
        setAvailableFirms([demoFirm]);
        setCurrentFirmIdState(demoFirm.firmId);
      }
    }
  }, [isAuthenticated, user, shouldFetchFirms, firmsError, firmsData, firmsLoading]);

  // Initialize firm context when auth is ready and either:
  // - API firms data is available (for admin/reviewer), or 
  // - No API call needed (for firm users using JWT token)
  useEffect(() => {
    if (!authLoading && (!shouldFetchFirms || !firmsLoading)) {
      initializeFirmContext();
    }
  }, [authLoading, firmsLoading, isAuthenticated, user, firmsData, firmsError, shouldFetchFirms, initializeFirmContext]);

  const setDefaultFirm = (firms: FirmInfo[]) => {
    if (firms.length === 0) return;

    // Try to restore saved firm selection
    const savedFirmId = localStorage.getItem('selectedFirmId');

    if (savedFirmId && firms.some(f => f.firmId === savedFirmId)) {
      setCurrentFirmIdState(savedFirmId);
    } else {
      // Default to first firm
      setCurrentFirmIdState(firms[0].firmId);
    }
  };

  const setFirmFromUrl = async (jobId: string, firms: FirmInfo[]) => {
    if (job?.firmId) {
      // Validate firm ID exists in available firms
      if (job.firmId && firms.some(f => f.firmId === job.firmId)) {
        setCurrentFirmIdState(job.firmId);
        localStorage.setItem('selectedFirmId', job.firmId);
        setError(null);
      } else {
        setDefaultFirm(firms)
      }
    }
  }


  const setCurrentFirmId = (firmId: string) => {
    // Validate firm ID exists in available firms
    const firm = availableFirms.find(f => f.firmId === firmId);
    if (!firm) {
      setError(`Invalid firm ID: ${firmId}`);
      return;
    }

    setCurrentFirmIdState(firmId);
    localStorage.setItem('selectedFirmId', firmId);
    setError(null);
  };

  const contextValue: FirmContextValue = {
    currentFirmId,
    availableFirms,
    setCurrentFirmId,
    isLoading,
    error
  };

  return (
    <FirmContext.Provider value={contextValue}>
      {children}
    </FirmContext.Provider>
  );
}

/**
 * Hook to access the firm context.
 * Throws an error if used outside of FirmProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFirm(): FirmContextValue {
  const context = useContext(FirmContext);
  if (context === undefined) {
    throw new Error('useFirm must be used within a FirmProvider');
  }
  return context;
}

/**
 * Hook to get the current firm ID.
 * Returns null if no firm is selected or still loading.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentFirmId(): string | null {
  const { currentFirmId, isLoading } = useFirm();
  return isLoading ? null : currentFirmId;
}

/**
 * Hook to check if a firm is currently selected and ready.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFirmReady(): { isReady: boolean; firmId: string | null } {
  const { currentFirmId, isLoading, error } = useFirm();
  return {
    isReady: !isLoading && !error && !!currentFirmId,
    firmId: currentFirmId
  };
}
